import { lazy, Suspense, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle, ArrowRight, Boxes, ClipboardList, FileText, PackagePlus, TrendingDown, Wallet,
} from 'lucide-react'
import { Card, EmptyState, Loading, Stat, StatusPill } from '@/components/ui'
import { useAuth } from '@/lib/auth'
import { usePendingActions, usePos, usePrs, useSiteNames, useStock } from '@/lib/data'
import { ROLE_LABEL, can, seesManySites, weekLabel } from '@/lib/domain'
import { SitePhotoCarousel, maySeeSitePhotos } from '@/components/site-photos/SitePhotoCarousel'
import { ddmmyyyy, inr, rupees, qty } from '@/lib/format'

// Loaded only when a board user opens the dashboard. Everyone else never
// downloads it, so the storeman's phone does not carry a charting library it
// will never render.
const BoardPanel = lazy(() => import('@/components/board/BoardPanel'))

const ROUTE: Record<string, string> = {
  // IST_PR is an auto-approved inter-site-transfer requisition with no transfer
  // raised against it yet. It is a requisition, so it opens on the requisition
  // screen — which is where the "Raise the transfer" button now lives.
  PR: '/prs', IST_PR: '/prs', PO: '/pos', GRN: '/grns', MIN: '/mins', MRN: '/mrns', INDENT: '/indents',
}

/** The pill colour on the queue, so an open loop does not read like an approval. */
const QUEUE_PILL: Record<string, string> = { IST_PR: 'pill-amber' }

export function Dashboard() {
  const { profile, site } = useAuth()
  const role = profile!.role
  // A Project Manager is neither head office nor single-site. Scoping him to
  // his profile site pinned his dashboard to head office, exactly as it did
  // the list screens.
  const scopeSite = seesManySites(role) ? undefined : (profile!.site_id ?? undefined)

  const { siteName } = useSiteNames()
  const { data: pending, loading: pendingLoading } = usePendingActions(role)
  const { data: stock, loading: stockLoading } = useStock(scopeSite ?? null)
  const { data: prs } = usePrs({ site_id: scopeSite })
  const { data: pos } = usePos({ site_id: scopeSite })

  // What is waiting on *this* person specifically.
  //
  // This used to be a hand-written list of four roles ending in `return false`,
  // which was fine when four roles were all there were. Every role the Board
  // created — Project Manager, Project Head, CMD, Accounts — fell through to
  // false and was told "nothing is waiting for you" while documents sat on
  // their desks. It now reads the stage the document is actually at.
  const myQueue = useMemo(() => pending.filter((p) => {
    if (role === 'admin') return true
    if (p.awaiting_role !== role) return false

    // Where the stage names ONE person — requisition stage 2 — it must be him.
    if (p.awaiting_user_id) return p.awaiting_user_id === profile!.id

    // Order stage 1 is the other Procurement Manager, never the one who
    // raised it. Showing a man his own order to counter-sign is the one thing
    // this stage exists to prevent.
    if (p.doc_type === 'PO' && p.awaiting_step === 1 && p.raised_by_id === profile!.id) return false

    // Site-bound roles only see their own site.
    if (role === 'storeman') return p.site_id === profile!.site_id

    return true
  }), [pending, role, profile])

  const belowMin = stock.filter((s) => s.below_minimum)
  const openPrs = prs.filter((p) => ['SUBMITTED', 'APPROVED', 'PARTIALLY_FULFILLED'].includes(p.status))
  const livePos = pos.filter((p) => ['VENDIN', 'VENDACK', 'PARTIALLY_RECEIVED'].includes(p.status))
  const poValue = livePos.reduce((a, p) => a + Number(p.total_amount || 0), 0)

  // The board panel: the CMD, the CXOs - and the administrator.
  //
  // Admin is here for a practical reason. The manual "record today's position"
  // control lives inside the panel and is admin-only, so leaving admin out of
  // the panel put the button somewhere its only user could never reach. It
  // also means the person who maintains the system can see what the board
  // sees, which is how you find out a board figure is wrong before the board
  // does.
  //
  // Everyone else gets exactly the page they had before, byte for byte.
  const showBoard = role === 'cmd' || role === 'cxo' || role === 'admin'

  const main = (
    <div className="space-y-5">
      {/* ---- greeting ---- */}
      <div>
        <h1 className="text-lg font-bold text-slate-800">
          {greeting()}, {profile!.full_name.split(' ')[0]}
        </h1>
        <p className="text-sm text-slate-500">
          {ROLE_LABEL[role]}
          {site ? ` · ${site.site_name}` : ''}
          {' · '}Business week {weekLabel()}
        </p>
      </div>

      {/* ---- headline numbers ----
          Head office (30-09-2026, migration 0185): the site photo carousel
          takes the right third on a tablet or laptop, the height of two rows,
          and the four numbers sit beside it as a 2 x 2 block. On a phone it
          sits above them, full width. Everybody else gets exactly the row of
          four they had before. */}
      <div className={maySeeSitePhotos(role)
        ? 'grid grid-cols-2 gap-3 md:grid-cols-3 md:[grid-auto-flow:dense]'
        : 'grid grid-cols-2 gap-3 lg:grid-cols-4'}>
        {maySeeSitePhotos(role) && (
          <SitePhotoCarousel className="col-span-2 md:col-span-1 md:col-start-3 md:row-span-2 md:row-start-1 md:self-center" />
        )}
        <Stat
          label="Waiting on you" value={myQueue.length}
          tone={myQueue.length ? 'warn' : 'good'}
          sub={myQueue.length ? 'Documents need your decision' : 'Nothing pending'}
          icon={<ClipboardList className="h-5 w-5" />}
        />
        <Stat
          label="Open requisitions" value={openPrs.length}
          sub="Submitted, approved or part-fulfilled"
          icon={<ClipboardList className="h-5 w-5" />}
        />
        <Stat
          label="Live purchase orders" value={livePos.length}
          sub={`${rupees(poValue)} with vendors`}
          icon={<FileText className="h-5 w-5" />}
        />
        <Stat
          label="Below minimum" value={belowMin.length}
          tone={belowMin.length ? 'bad' : 'good'}
          sub={scopeSite ? 'At your site'
            : role === 'project_manager' ? 'Across your sites'
              : 'Across all sites'}
          icon={<TrendingDown className="h-5 w-5" />}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* ---- action queue ---- */}
        <Card
          className="lg:col-span-2"
          title="Your action queue"
          subtitle="Documents that cannot move until you decide"
        >
          {pendingLoading && <Loading />}
          {!pendingLoading && myQueue.length === 0 && (
            <EmptyState
              title="Nothing is waiting for you"
              message="When a site raises a requisition or receives material, it will appear here."
            />
          )}
          {myQueue.length > 0 && (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Document</th><th>Site</th><th>Raised by</th>
                    <th>Date</th><th className="num">Value</th><th />
                  </tr>
                </thead>
                <tbody>
                  {myQueue.slice(0, 12).map((p) => (
                    <tr key={`${p.doc_type}-${p.doc_id}`}>
                      <td>
                        <span className={`${QUEUE_PILL[p.doc_type] ?? 'pill-grey'} mr-1.5`}>
                          {p.doc_type === 'IST_PR' ? 'TRANSFER DUE' : p.doc_type}
                        </span>
                        <span className="font-mono text-xs">{p.doc_id}</span>
                      </td>
                      <td className="text-xs" title={p.site_id}>{siteName(p.site_id)}</td>
                      <td className="max-w-[180px] truncate text-xs text-slate-500">{p.raised_by ?? '—'}</td>
                      <td className="text-xs">{ddmmyyyy(p.doc_date)}</td>
                      <td className="num text-xs">{p.amount ? inr(p.amount) : '—'}</td>
                      <td className="text-right">
                        <Link to={`${ROUTE[p.doc_type] ?? '/'}/${p.doc_id}`} className="btn-secondary btn-sm">
                          Open <ArrowRight className="h-3 w-3" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* ---- quick actions ---- */}
        <Card title="Quick actions" subtitle="What your role does most often">
          <div className="space-y-2">
            {can(role, 'raiseIndent') && <QuickLink to="/indents/new" icon={ClipboardList} label="Raise a site indent" hint="Tell the store-man what the work needs" />}
            {can(role, 'createPr') && <QuickLink to="/prs/new" icon={ClipboardList} label="Raise a purchase requisition" hint="Consolidate what the site cannot fulfil" />}
            {can(role, 'createPo') && <QuickLink to="/pos/new" icon={FileText} label="Create a purchase order" hint="From an approved PR, or standalone for urgent buys" />}
            {can(role, 'createGrn') && <QuickLink to="/grns/new" icon={PackagePlus} label="Receive material (GRN)" hint="Against an acknowledged PO, or ad-hoc" />}
            {can(role, 'createMin') && <QuickLink to="/mins/new" icon={Boxes} label="Issue material (MIN)" hint="Hand material to the RCM for site work" />}
            {can(role, 'manageIst') && <QuickLink to="/ists/new" icon={Boxes} label="Start an inter-site transfer" hint="Move stock from a surplus site" />}
            {can(role, 'runWeekly') && <QuickLink to="/reports" icon={Wallet} label="Weekly consumption report" hint="Friday close-out for the CFO" />}
            <QuickLink to="/inventory" icon={Boxes} label="Check stock" hint="Live position with reorder flags" />
          </div>
        </Card>
      </div>

      {/* ---- reorder alerts ---- */}
      {belowMin.length > 0 && (
        <Card
          title={<span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Materials below minimum stock</span>}
          subtitle="Consider raising a requisition before work stops"
          actions={<Link to="/inventory" className="btn-secondary btn-sm">Full stock view</Link>}
        >
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Site</th><th>Material</th><th>Code</th>
                  <th className="num">On hand</th><th className="num">Minimum</th><th className="num">Short by</th>
                </tr>
              </thead>
              <tbody>
                {belowMin.slice(0, 15).map((s) => (
                  <tr key={`${s.site_id}-${s.material_id}`}>
                    <td className="text-xs">{s.site_name}</td>
                    <td className="text-sm font-medium">{s.item_name}</td>
                    <td className="font-mono text-xs text-slate-500">{s.item_code}</td>
                    <td className="num font-semibold text-red-600">{qty(s.quantity_on_hand)} {s.uom}</td>
                    <td className="num text-slate-500">{qty(s.min_stock_level)}</td>
                    <td className="num font-medium">{qty(s.min_stock_level - s.quantity_on_hand)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ---- recent documents ---- */}
      <div className="grid gap-5 lg:grid-cols-2">
        <Card title="Latest requisitions" actions={<Link to="/prs" className="btn-secondary btn-sm">All</Link>}>
          <RecentList
            rows={prs.slice(0, 6).map((p) => ({
              id: p.id, status: p.status, meta: `${siteName(p.site_id)} · ${ddmmyyyy(p.date_created)}`,
              right: rupees(p.total_estimated_amount), to: `/prs/${p.id}`,
            }))}
            empty="No requisitions yet."
          />
        </Card>
        <Card title="Latest purchase orders" actions={<Link to="/pos" className="btn-secondary btn-sm">All</Link>}>
          <RecentList
            rows={pos.slice(0, 6).map((p) => ({
              id: p.id, status: p.status, meta: `${siteName(p.site_id)} · ${ddmmyyyy(p.date_created)}`,
              right: rupees(p.total_amount), to: `/pos/${p.id}`,
            }))}
            empty="No purchase orders yet."
          />
        </Card>
      </div>
    </div>
  )

  if (!showBoard) return main

  return (
    <div className="grid gap-5 2xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="min-w-0">{main}</div>
      <div className="min-w-0">
        <Suspense fallback={<Loading label="Building the board view…" />}>
          <BoardPanel />
        </Suspense>
      </div>
    </div>
  )
}

function QuickLink({ to, icon: Icon, label, hint }: {
  to: string; icon: typeof Boxes; label: string; hint: string
}) {
  return (
    <Link to={to} className="flex items-start gap-3 rounded-lg border border-slate-200 px-3 py-2.5 hover:border-oval-300 hover:bg-oval-50">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-oval-600" />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-slate-800">{label}</span>
        <span className="block text-xs text-slate-500">{hint}</span>
      </span>
      <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-slate-300" />
    </Link>
  )
}

function RecentList({ rows, empty }: {
  rows: Array<{ id: string; status: string; meta: string; right: string; to: string }>
  empty: string
}) {
  if (rows.length === 0) return <p className="py-4 text-center text-sm text-slate-500">{empty}</p>
  return (
    <ul className="divide-y divide-slate-100">
      {rows.map((r) => (
        <li key={r.id}>
          <Link to={r.to} className="flex items-center gap-3 py-2.5 hover:bg-slate-50">
            <span className="min-w-0 flex-1">
              <span className="block truncate font-mono text-xs font-medium text-slate-800">{r.id}</span>
              <span className="block text-xs text-slate-500">{r.meta}</span>
            </span>
            <StatusPill status={r.status} />
            <span className="hidden text-xs font-semibold tabular-nums text-slate-700 sm:block">{r.right}</span>
          </Link>
        </li>
      ))}
    </ul>
  )
}

function greeting() {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}
